import {
  ClientType,
  ENDPOINTS,
  generateOptions,
  OperationReportType,
  Status,
  WebTable
} from '@constants';
import { Prototype } from '@core';
import {
  useExportOperationReportData,
  useMutateFetchOperationReportData
} from '@hooks/report/operation';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import DateTimeInput from 'components/CustomInput/DateTime';
import { map } from 'lodash';
import { memo, useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import trans from 'translation';
import { KChip, KContainer, KForm, KGrid, KInput, KLabel } from 'uikit';

import Item from '../../_components/Item';
import {
  ImportDateType,
  IReportOperationFilter,
  JobContainerStatus
} from '../../helpers';

interface IFormData extends Omit<IReportOperationFilter, 'consignors'> {}

const ReportImpEmptyYardDehireDate = () => {
  const {
    data,
    mutate: mutateGenerate,
    isLoading
  } = useMutateFetchOperationReportData();

  const { mutate: mutateExport, isLoading: isExporting } =
    useExportOperationReportData();

  const colDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: trans('job_number'),
        field: 'jobNumber',
        pinned: true
      },
      {
        headerName: trans('container_number'),
        field: 'containerNumber',
        pinned: true,
        cellClass: 'ag-cell-full-width',
        cellRenderer: ({ value = null, node }: ICellRendererParams) => {
          return (
            <KContainer.View
              dp="flex"
              row
              justifyContent={node.rowPinned ? 'flex-end' : 'flex-start'}
            >
              <KLabel.Text
                typo={node.rowPinned ? 'TextNmBold' : 'TextNmNormal'}
              >
                {value}
              </KLabel.Text>
            </KContainer.View>
          );
        }
      },
      {
        headerName: trans('container_size'),
        field: 'containerSize'
      },
      {
        headerName: trans('dehire_date_time'),
        field: 'timeslotDateTime' // Added
      },
      {
        headerName: trans('empty_yard_code'),
        field: 'emptyYardClientCode' // Added
      },
      {
        headerName: trans('empty_yard_name'),
        field: 'emptyYardClientName' // Added
      },
      {
        headerName: trans('empty_yard_address'),
        field: 'emptyYardClientAddress' // Added
      },
      {
        headerName: trans('job_type'),
        field: 'jobType'
      },
      {
        headerName: trans('drop_mode'),
        field: 'dropMode'
      },
      {
        headerName: trans('delivery_pickup_warehouse_code'),
        field: 'deliveryPickUpWarehouseCode'
      },
      {
        headerName: trans('delivery_pickup_warehouse_name'),
        field: 'deliveryPickUpWarehouseName'
      },
      {
        headerName: trans('delivery_pickup_warehouse_address'),
        field: 'deliveryPickUpWarehouseAddress',
        minWidth: 200
      },
      {
        headerName: trans('consignee_code'),
        field: 'consignClientCode'
      },
      {
        headerName: trans('consignee_name'),
        field: 'consignClientName'
      },
      {
        headerName: trans('consignee_address'),
        field: 'consignClientAddress',
        minWidth: 200
      },
      {
        headerName: trans('account_receivable_code'),
        field: 'arClientCode'
      },
      {
        headerName: trans('account_receivable_name'),
        field: 'arClientName'
      },
      {
        headerName: trans('account_receivable_address'),
        field: 'arClientAddress',
        minWidth: 200
      },
      {
        headerName: trans('agent_code'),
        field: 'agentClientCode'
      },
      {
        headerName: trans('agent_name'),
        field: 'agentClientName'
      },
      {
        headerName: trans('agent_address'),
        field: 'agentClientAddress',
        minWidth: 200
      },
      {
        headerName: trans('customer_reference'),
        field: 'referenceNumber'
      },
      {
        headerName: trans('vessel'),
        field: 'vessel'
      },
      {
        headerName: trans('lloyd'),
        field: 'lloyd'
      },
      {
        headerName: trans('voyage'),
        field: 'voyage'
      },
      {
        headerName: trans('unloco_board_of_loading'),
        field: 'unlocoBoardOfLoading'
      },
      {
        headerName: trans('unloco_board_of_discharge'),
        field: 'unlocoBoardOfDischarge'
      },
      {
        headerName: trans('eta'),
        field: 'eta'
      },
      {
        headerName: trans('etd'),
        field: 'etd'
      },
      {
        headerName: trans('imp_available_date'),
        field: 'avail'
      },
      {
        headerName: trans('imp_storage_start_date'),
        field: 'stor'
      },
      {
        headerName: trans('1st_free_date'),
        field: 'firstFreeDate'
      },
      {
        headerName: trans('discharge_date'),
        field: 'dischargeDate'
      },
      {
        headerName: trans('reefer_cutoff_date'),
        field: 'reeferCutoffDate'
      },
      {
        headerName: trans('empty_receival_date'),
        field: 'emptyReceivalCommencementDate'
      },
      {
        headerName: trans('empty_cutoff_date'),
        field: 'emptyCutoffDate'
      },
      {
        headerName: trans('hazardous_receival_date'),
        field: 'hazardousReceivalCommencementDate'
      },
      {
        headerName: trans('hazardous_cutoff_date'),
        field: 'hazardousCutoffDate'
      },
      {
        headerName: trans('container_status'),
        field: 'containerStatus'
      },
      {
        headerName: trans('invoice_status'),
        field: 'invoiceStatus'
      }
      // {
      //   headerName: trans('delivery_date_time'),
      //   field: 'deliveryDateTime' // Removed: not in required columns
      // }
    ],
    []
  );

  const methods = useForm<IFormData>({
    defaultValues: {
      isExport: false,
      agents: [],
      consignees: [],
      warehouses: [],
      vessels: [],
      dateType: ImportDateType.DeliveryDateTime,
      fromDate: '',
      toDate: '',
      jobStatus: JobContainerStatus.Active,
      containerStatus: JobContainerStatus.Active,
      containers: '',
      customerReferences: ''
    }
  });

  const [fromDate] = useWatch({
    control: methods.control,
    name: ['fromDate']
  });

  const onFormValid = useCallback(
    (_data: IFormData) => {
      const {
        isExport,
        agents,
        consignees,
        warehouses,
        vessels,
        fromDate: _fromDate,
        toDate: _toDate,
        ...rest
      } = _data;

      const agentIds = map(agents, 'id');
      const consigneeIds = map(consignees, 'id');
      const warehouseIds = map(warehouses, 'id');
      const vesselIds = map(vessels, 'id');

      const _params = {
        reportExcelType: OperationReportType.IMPORT_EMPTY_YARD_DEHIRE_DATE,
        agentIds,
        consigneeIds,
        warehouseIds,
        vesselIds,
        fromDate: Prototype.date.formatDB(_fromDate),
        toDate: Prototype.date.formatDB(_toDate),
        ...rest
      };

      const mutate = isExport ? mutateExport : mutateGenerate;

      mutate(_params);

      // if (isExport) {
      //   mutateExport({
      //     reportExcelType: OperationReportType.IMPORT_EMPTY_YARD_DEHIRE_DATE,
      //     agentIds,
      //     consigneeIds,
      //     warehouseIds,
      //     vesselIds,
      //     fromDate: Prototype.date.formatDB(_fromDate),
      //     toDate: Prototype.date.formatDB(_toDate),
      //     ...rest
      //   });
      // } else {
      //   mutate({
      //     reportExcelType: OperationReportType.IMPORT_EMPTY_YARD_DEHIRE_DATE,
      //     agentIds,
      //     consigneeIds,
      //     warehouseIds,
      //     vesselIds,
      //     fromDate: Prototype.date.formatDB(_fromDate),
      //     toDate: Prototype.date.formatDB(_toDate),
      //     ...rest
      //   });
      // }
    },
    [mutateExport, mutateGenerate]
  );

  const renderPinnedBottomRowDataFunc = useCallback(
    (_data: any) => [
      {
        jobNumber: trans('total_jobs'),
        containerNumber: _data?.totalJob ?? 0
      },
      {
        jobNumber: trans('total_containers'),
        containerNumber: _data?.totalContainer ?? 0
      },
      {
        jobNumber: trans('total_20ft_containers'),
        containerNumber: _data?.total20ftContainers ?? 0
      },
      {
        jobNumber: trans('total_40ft_containers'),
        containerNumber: _data?.total40ftContainers ?? 0
      }
    ],
    []
  );

  return (
    <FormProvider {...methods}>
      <KForm onSubmit={methods.handleSubmit(onFormValid)}>
        <KGrid.Container>
          <KGrid.Item xs={3}>
            <Controller
              name="agents"
              control={methods.control}
              render={({ field }) => (
                <KInput.Autocomplete
                  {...field}
                  label={trans('agent')}
                  multiple
                  renderTags={(tagValue, getTagProps) => {
                    return tagValue?.map((i: any, index) => {
                      const isActive = i?.status === Status.Active;
                      return (
                        <KChip
                          key={i.id}
                          {...getTagProps({ index })}
                          label={i.name}
                          isLink
                          onPress={() => {
                            window.open(`/admin/clients/${i.id}`);
                          }}
                          color={isActive ? undefined : 'secondary'}
                        />
                      );
                    });
                  }}
                  apiURL={ENDPOINTS.client()}
                  apiParams={{
                    typeIn: [
                      ClientType.CustomsAgent,
                      ClientType.ForwarderAgent
                    ],
                    status: Status.Active
                  }}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="consignees"
              control={methods.control}
              render={({ field }) => (
                <KInput.Autocomplete
                  {...field}
                  label={trans('consignee')}
                  multiple
                  renderTags={(tagValue, getTagProps) => {
                    return tagValue?.map((i: any, index) => {
                      const isActive = i?.status === Status.Active;
                      return (
                        <KChip
                          key={i.id}
                          {...getTagProps({ index })}
                          label={i.name}
                          isLink
                          onPress={() => {
                            window.open(`/admin/clients/${i.id}`);
                          }}
                          color={isActive ? undefined : 'secondary'}
                        />
                      );
                    });
                  }}
                  apiURL={ENDPOINTS.client()}
                  apiParams={{
                    typeIn: [ClientType.Consignee],
                    status: Status.Active
                  }}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="warehouses"
              control={methods.control}
              render={({ field }) => (
                <KInput.Autocomplete
                  {...field}
                  label={trans('warehouse')}
                  multiple
                  renderTags={(tagValue, getTagProps) => {
                    return tagValue?.map((i: any, index) => {
                      const isActive = i?.status === Status.Active;
                      return (
                        <KChip
                          key={i.id}
                          {...getTagProps({ index })}
                          label={i.name}
                          isLink
                          onPress={() => {
                            window.open(`/admin/clients/${i.id}`);
                          }}
                          color={isActive ? undefined : 'secondary'}
                        />
                      );
                    });
                  }}
                  apiURL={ENDPOINTS.client()}
                  apiParams={{
                    typeIn: [ClientType.Warehouse],
                    status: Status.Active
                  }}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="vessels"
              control={methods.control}
              render={({ field }) => (
                <KInput.Autocomplete
                  {...field}
                  label={trans('vessel')}
                  multiple
                  getOptionLabel={(option: any) => option.vessel}
                  renderTags={(tagValue, getTagProps) => {
                    return tagValue?.map((i: any, index) => {
                      return (
                        <KChip
                          key={i.id}
                          {...getTagProps({ index })}
                          label={i.vessel}
                          isLink
                          onPress={() => {}}
                        />
                      );
                    });
                  }}
                  apiURL={ENDPOINTS.vessel()}
                  apiParams={{
                    status: Status.Active
                  }}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={2}>
            <Controller
              name="dateType"
              control={methods.control}
              render={({ field }) => (
                <KInput.TextField
                  {...field}
                  label={trans('date_type')}
                  options={generateOptions(ImportDateType)}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="fromDate"
              control={methods.control}
              render={({ field }) => (
                <DateTimeInput
                  {...field}
                  label={trans('from_date')}
                  kind="date"
                  // disabled={!dateType}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={3}>
            <Controller
              name="toDate"
              control={methods.control}
              render={({ field }) => (
                <DateTimeInput
                  {...field}
                  label={trans('to_date')}
                  kind="date"
                  // disabled={!dateType || !fromDate}
                  minDate={Prototype.date.toMoment(fromDate) ?? undefined}
                />
              )}
            />
          </KGrid.Item>

          <KGrid.Item xs={2}>
            <Controller
              name="jobStatus"
              control={methods.control}
              render={({ field }) => (
                <KInput.TextField
                  {...field}
                  label={trans('job_status')}
                  options={generateOptions(JobContainerStatus)}
                />
              )}
            />
          </KGrid.Item>
          <KGrid.Item xs={2}>
            <Controller
              name="containerStatus"
              control={methods.control}
              render={({ field }) => (
                <KInput.TextField
                  {...field}
                  label={trans('container_status')}
                  options={generateOptions(JobContainerStatus)}
                />
              )}
            />
          </KGrid.Item>

          <KGrid.Item xs={6}>
            <Controller
              control={methods.control}
              name="containers"
              render={({ field }) => {
                return (
                  <KInput.TextField {...field} label={trans('container')} />
                );
              }}
            />
          </KGrid.Item>

          <KGrid.Item xs={6}>
            <Controller
              control={methods.control}
              name="customerReferences"
              render={({ field }) => {
                return (
                  <KInput.TextField
                    {...field}
                    label={trans('customer_reference')}
                  />
                );
              }}
            />
          </KGrid.Item>

          <KGrid.Item xs={12}>
            <Item
              name={WebTable.OPERATION_REPORT_IMPORT_EMPTY_YARD_DEHIRE_DATE}
              colDefs={colDefs}
              data={data}
              isExporting={isExporting}
              isGenerating={isLoading}
              onSubmit={onFormValid}
              renderPinnedBottomRowDataFunc={renderPinnedBottomRowDataFunc}
            />
          </KGrid.Item>
        </KGrid.Container>
      </KForm>
    </FormProvider>
  );
};

export default memo(ReportImpEmptyYardDehireDate);
